/**
 * Designer draft persistence — the in-progress design (form, paths, step) is
 * autosaved into cycle metadata so leaving the Designer (Detail view, refresh,
 * back button) never loses work. The draft is stripped at launch.
 */

import type { DegapCycle } from '@/types/prioritization/degapCycle';
import type { Track } from '@/types/prioritization/track';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import type {
  CycleDesignerFormState,
  DesignerPath,
  DesignerStep,
  PathSlotId,
  TrackParams,
} from '../types';
import { DEFAULT_TRACK_PARAMS, isPathSlotId, pathColorForSlot, pathSlotIdForIndex } from '../types';
import {
  DESIGNER_COLOR_METADATA_KEY,
  DESIGNER_PATH_ASSIGNMENTS_KEY,
  DESIGNER_PATH_ORG_UNITS_KEY,
  DESIGNER_PATH_SLOT_KEY,
  DESIGNER_PATH_WINDOWS_KEY,
  DESIGNER_TRACK_PARAMS_KEY,
} from './launchCycleDesigner';

export const DESIGNER_DRAFT_METADATA_KEY = 'designerDraft';

export interface DesignerDraft {
  form: CycleDesignerFormState;
  paths: DesignerPath[];
  step: DesignerStep;
  savedAt: string;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isParticipantRef(v: unknown): v is CatchballParticipantRef {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    (r.type === 'user' || r.type === 'group') &&
    typeof r.id === 'string' &&
    typeof r.displayName === 'string'
  );
}

/** Rebuild per-node assignments from untrusted metadata; drops anything malformed. */
function sanitizeNodeAssignments(raw: unknown): Record<string, CatchballParticipantRef> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, CatchballParticipantRef> = {};
  for (const [nodeId, ref] of Object.entries(raw as Record<string, unknown>)) {
    if (isParticipantRef(ref)) out[nodeId] = ref;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Rebuild per-node response-window overrides from untrusted metadata; keeps only
 * entries whose value is a finite number > 0 (rounded), matching setNodeWindow's
 * own validation. Drops zero/negative/non-numeric values.
 */
function sanitizeNodeWindows(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, number> = {};
  for (const [nodeId, days] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof days === 'number' && Number.isFinite(days) && days > 0) {
      out[nodeId] = Math.round(days);
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Rebuild a path from untrusted metadata; null if it can't be trusted. */
function sanitizePath(raw: unknown): DesignerPath | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const id = p.id as PathSlotId;
  // Any a…z / aa… id is valid — there is no fixed slot list to be a member of.
  if (typeof id !== 'string' || !isPathSlotId(id)) return null;
  if (!isStringArray(p.nodes) || p.nodes.length === 0) return null;
  const params = (p.params && typeof p.params === 'object' ? p.params : {}) as Partial<TrackParams>;
  return {
    id,
    name: typeof p.name === 'string' && p.name.trim() ? p.name : `Track ${id.toUpperCase()}`,
    color: typeof p.color === 'string' && p.color ? p.color : pathColorForSlot(id),
    nodes: p.nodes,
    params: { ...DEFAULT_TRACK_PARAMS, ...params },
    nodeAssignments: sanitizeNodeAssignments(p.nodeAssignments),
    nodeWindows: sanitizeNodeWindows(p.nodeWindows),
  };
}

/** Read and validate the autosaved draft from cycle metadata. */
export function readDesignerDraft(cycle: DegapCycle | undefined): DesignerDraft | null {
  if (!cycle?.metadata || typeof cycle.metadata !== 'object') return null;
  const raw = (cycle.metadata as Record<string, unknown>)[DESIGNER_DRAFT_METADATA_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;

  const form = d.form;
  if (!form || typeof form !== 'object' || typeof (form as Record<string, unknown>).cycleName !== 'string') {
    return null;
  }

  const paths = Array.isArray(d.paths)
    ? d.paths.map(sanitizePath).filter((p): p is DesignerPath => p !== null)
    : [];
  if (paths.length === 0) return null;

  const stepRaw = Number(d.step);
  const step: DesignerStep = stepRaw >= 1 && stepRaw <= 4 ? (stepRaw as DesignerStep) : 1;

  return {
    form: form as CycleDesignerFormState,
    paths,
    step,
    savedAt: typeof d.savedAt === 'string' ? d.savedAt : '',
  };
}

/**
 * Reopen support: rebuild the designed paths from the Tracks a launch
 * created, so reopening the Designer shows the actual design instead of a blank
 * default path. Slot comes from track metadata when present; otherwise slots are
 * assigned in order.
 */
export function pathsFromLaunchedTracks(tracks: Track[]): DesignerPath[] {
  const used = new Set<PathSlotId>();
  const paths: DesignerPath[] = [];
  for (const t of tracks) {
    if (t.status === 'archived') continue;
    const meta = (t.metadata ?? {}) as Record<string, unknown>;
    const nodes = meta[DESIGNER_PATH_ORG_UNITS_KEY];
    if (!isStringArray(nodes) || nodes.length === 0) continue;
    const slotRaw = meta[DESIGNER_PATH_SLOT_KEY] as PathSlotId;
    let slot: PathSlotId | undefined =
      typeof slotRaw === 'string' && isPathSlotId(slotRaw) && !used.has(slotRaw)
        ? slotRaw
        : undefined;
    if (!slot) {
      // Walk indices until a free id appears — never give up, there is no cap.
      let i = 0;
      while (used.has(pathSlotIdForIndex(i))) i += 1;
      slot = pathSlotIdForIndex(i);
    }
    used.add(slot);
    const params = (
      meta[DESIGNER_TRACK_PARAMS_KEY] && typeof meta[DESIGNER_TRACK_PARAMS_KEY] === 'object'
        ? meta[DESIGNER_TRACK_PARAMS_KEY]
        : {}
    ) as Partial<TrackParams>;
    paths.push({
      id: slot,
      name: t.name,
      color:
        typeof meta[DESIGNER_COLOR_METADATA_KEY] === 'string' && meta[DESIGNER_COLOR_METADATA_KEY]
          ? (meta[DESIGNER_COLOR_METADATA_KEY] as string)
          : pathColorForSlot(slot),
      nodes,
      params: { ...DEFAULT_TRACK_PARAMS, ...params },
      nodeAssignments: sanitizeNodeAssignments(meta[DESIGNER_PATH_ASSIGNMENTS_KEY]),
      nodeWindows: sanitizeNodeWindows(meta[DESIGNER_PATH_WINDOWS_KEY]),
    });
  }
  return paths;
}

/** Serializable snapshot used for both change detection and the saved payload. */
export function draftSnapshot(
  form: CycleDesignerFormState,
  paths: DesignerPath[],
  step: DesignerStep,
): string {
  return JSON.stringify({ form, paths, step });
}
